import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Container } from 'reactstrap';
import {
  FiArrowLeft,
  FiBriefcase,
  FiCheck,
  FiChevronDown,
  FiCode,
  FiEye,
  FiGrid,
  FiLayers,
  FiMessageSquare,
  FiMonitor,
  FiPlay,
  FiSliders,
  FiTrendingUp,
  FiUsers,
  FiZap,
} from 'react-icons/fi';
import './GenAIHe.css';

const sectionLinks = [
  { href: '#genai-features', label: 'תכונות' },
  { href: '#genai-use-cases', label: 'שימושים' },
  { href: '#genai-pricing', label: 'חבילות' },
  { href: '#genai-faq', label: 'שאלות נפוצות' },
];

const valueStrip = [
  '100 קרדיטים לפיתוח AI כלולים',
  'סביבת עבודה (Workspace) כלולה',
  'אפשרויות ענן מנוהל ו-VM ייעודי',
  'חבילות ברורות לכל שלב בפרויקט',
];

const promiseStats = [
  { value: '100 נקודות', label: 'בינה מלאכותית כלולות בכל חבילה' },
  { value: 'סביבת עבודה', label: 'לבנות, לבדוק ולשפר במקום אחד' },
  { value: 'הפרדה', label: 'לבנייה, תצוגה מקדימה ופרודקשן' },
  { value: 'סביבה מנוהלת', label: 'בלי להתעסק עם שרתים, SSL או פריסה' },
];

const optionCards = [
  {
    title: 'GenAI Cloud',
    badge: 'התחלה מהירה',
    description: 'סביבת ריצה מנוהלת בענן',
    points: [
      'הכי מתאים לדפי נחיתה, אתרים פשוטים, אפליקציות קלות ו-MVPs ראשוניים',
      'עלות חודשית נמוכה יותר',
      'ללא VM ייעודי',
    ],
  },
  {
    title: 'GenAI Dedicated',
    badge: 'VM ייעודי',
    description: 'CPU, RAM, SSD ותעבורה מוגדרים מראש',
    points: [
      'הכי מתאים לאפליקציות עסקיות ועומסי עבודה כבדים יותר',
      'משאבים צפויים ויציבים יותר',
      'יותר שליטה',
    ],
  },
];

const steps = [
  {
    title: 'תארו מה אתם רוצים לבנות',
    description: 'ספרו ל־GenAI מה אתם רוצים לבנות — דפים, תהליכים, פיצ׳רים או זרימת עבודה.',
    icon: FiMessageSquare,
  },
  {
    title: 'בנו ושפרו עם AI',
    description: 'השתמשו ב-AI כדי ליצור, לדייק, לתקן ולהתקדם מהר יותר לגרסה עובדת.',
    icon: FiCode,
  },
  {
    title: 'בדקו ב-Workspace',
    description: 'סקרו שינויים, השוו גרסאות ושפרו את האפליקציה בסביבת עבודה מעשית.',
    icon: FiEye,
  },
  {
    title: 'הריצו בסביבה שמתאימה לפרויקט שלכם',
    description:
      'השיקו בלחיצת כפתור. התחילו עם GenAI Cloud בסביבת ריצה פשוטה ומנוהלת. כשהאפליקציה שלכם תצטרך יותר כוח, בידוד או משאבים ייעודיים — תוכלו לעבור ל־GenAI Dedicated.',
    icon: FiPlay,
  },
];

const benefits = [
  {
    title: 'בנו מהר יותר עם AI',
    description: 'עברו מרעיון למוצר שמיש מהר יותר בעזרת AI שעוזר לכם ליצור, לשפר ולתקן.',
    icon: FiZap,
  },
  {
    title: 'סביבת עבודה להתקדמות אמיתית',
    description: 'בדקו גרסאות, סקרו שינויים והמשיכו להתקדם בלי כלים מפוזרים.',
    icon: FiMonitor,
  },
  {
    title: 'התחילו פשוט, גדלו כשצריך',
    description:
      'התחילו עם GenAI Cloud להגדרה מהירה ומנוהלת. כשהפרויקט שלכם יצטרך יותר כוח, בידוד חזק יותר או משאבים ייעודיים, שדרגו ל-GenAI Dedicated.',
    icon: FiTrendingUp,
  },
  {
    title: 'אין צורך להתחיל מתשתיות',
    description: 'דלגו על הגדרת שרתים מוקדמת והתמקדו בבנייה לפני שהתשתית הופכת למשימה העיקרית.',
    icon: FiLayers,
  },
  {
    title: 'בנוי לרעיונות ולפרויקטים אמיתיים',
    description: 'השתמשו בו עבור MVPs, כלים פנימיים, עבודת לקוחות, אפליקציות עסקיות וניסויי מוצר.',
    icon: FiGrid,
  },
  {
    title: 'תמחור ברור וגמיש',
    description: 'כל חבילה כוללת 100 קרדיטים לפיתוח AI, וניתן להוסיף עוד כשצריך.',
    icon: FiSliders,
  },
];

const oldWay = [
  'מתחילים מהגדרות במקום מהרעיון',
'בונים סביבת בדיקות מאפס',
'מתעכבים לפני שאפשר להציג גרסה ללקוח',
'מתעסקים בשרתים, פריסה ותשתיות מוקדם מדי',
'כל שינוי דרש כתיבה ועריכה ידנית של קוד',
'מגיעים לאט יותר מרעיון לפידבק',
];

const genaiWay = [
  'מתחילים עם רעיון המוצר',
  'בונים ומשפרים עם AI',
  'בודקים ב-Workspace',
  'מריצים בסביבה הנכונה',
  'משדרגים כשהפרויקט צריך יותר',
  'מציגים תוצאות מהר יותר',
];

const audiences = [
  {
    title: 'יזמים',
    description: 'בדקו רעיון, השיקו MVP, והגיעו לגרסה עובדת מהר יותר.',
    icon: FiTrendingUp,
  },
  {
    title: 'עסקים קטנים',
    description: 'צרו כלים פנימיים, דשבורדים ואפליקציות עסקיות עם GenAI.',
    icon: FiBriefcase,
  },
  {
    title: 'סוכנויות',
    description: 'בנו מהר יותר ללקוחות, הציגו גרסאות עובדות מוקדם יותר, והרחיבו פרויקטים רציניים בהמשך.',
    icon: FiUsers,
  },
  {
    title: 'מפתחים',
    description: 'חסכו זמן בהקמה, בדקו רעיונות מהר יותר, והתקדמו בקלות בשלבים הראשונים של המוצר.',
    icon: FiCode,
  },
  {
    title: 'בוני מוצרים',
    description: 'הפכו מושגים גולמיים לתוכנה שמישה והמשיכו לשפר ככל שהפרויקט גדל.',
    icon: FiSliders,
  },
];

const useCaseGroups = [
  {
    title: 'GenAI Cloud',
    subtitle: 'הכי מתאים להתחלות מהירות, פרויקטים פשוטים ורעיונות בשלבים מוקדמים.',
    description:
      'השתמשו ב-GenAI Cloud כדי לאמת רעיונות, לבנות אפליקציות פשוטות, לבדוק קונספטים ללקוחות ולהשיק פרויקטים קלים ללא ניהול תשתיות.',
    items: [
      'דפי נחיתה',
      'אתרים עסקיים',
      'אב טיפוס למוצר',
      'MVPs מוקדמים',
      'דפי קמפיין',
      'דפי שירות',
      'דשבורדים פשוטים',
      'תצוגות מקדימות ללקוחות',
      'כלים פנימיים בסיסיים',
      'פרויקטים לאימות רעיונות',
    ],
    tags: ['התחלה מהירה', 'ענן מנוהל', 'עלות נמוכה', 'השקה פשוטה'],
  },
  {
    title: 'GenAI Dedicated',
    subtitle: 'הכי מתאים לפרויקטים רציניים, אפליקציות עסקיות ומשאבים ייעודיים.',
    description:
      'עברו ל-GenAI Dedicated כשהאפליקציה שלכם הופכת לחשובה יותר, צריכה יותר כוח, בידוד חזק יותר או VM ייעודי לשימוש בייצור.',
    items: [
      'פורטלי לקוחות',
      'דשבורדים לניהול',
      'מערכות ניהול הזמנות',
      'מערכות מעקב לידים',
      'כלי הצעות מחיר',
      'מערכות הזמנת תורים',
      'דשבורדים לדוחות',
      'סביבות עבודה תפעוליות',
      'אפליקציות SaaS מוקדמות',
      'פרויקטי ייצור ללקוחות',
      'אפליקציות עם שימוש כבד יותר',
      'פרויקטים שדורשים משאבים ייעודיים',
    ],
    tags: ['VM ייעודי', 'יותר בידוד', 'יותר משאבים', 'מכוון לייצור'],
  },
];

const cloudStarterPlan = {
  name: 'GenAI Cloud Starter',
  tag: 'התחלה מומלצת',
  price: '₪ 89',
  description:
    'הכי מתאים לדפי נחיתה, אתרים פשוטים, MVPs מוקדמים ואפליקציות קלות שצריכות סביבה מנוהלת ומהירה.',
  cta: 'התחילו עם Cloud',
  href: 'https://my.epicfinal.com/index.php?rp=%2Fstore%2Fmanaged-genai-software-builder%2Fgenai-cloud&language=hebrew&currency=2',
  specs: [
    '100 קרדיטים לפיתוח AI',
    'סביבת עבודה (Workspace)',
    'סביבת ריצה מנוהלת בענן',
    'סביבות נפרדות לבנייה, תצוגה מקדימה וייצור',
    'דומיין זמני',
    'חיבור דומיין מותאם אישית',
    'SSL',
    'תמיכה בחיבור למסד נתונים חיצוני, אחסון ו-API',
    'בכפוף לשימוש הוגן',
  ],
};

const privatePlans = [
  {
    name: 'GenAI Dedicated Core',
    tag: '',
    price: '₪ 199',
    description: 'עבור MVP ראשוני, אפליקציה קלה או פרויקט לעסק קטן שצריך VM ייעודי.',
    cta: 'התחילו עם Dedicated Core',
    href: 'https://my.epicfinal.com/index.php?rp=/store/managed-genai-software-builder/genai-core&language=hebrew&currency=2',
    specs: [
      'סביבת VM ייעודית',
      '2 vCPU',
      '4 GB RAM',
      'נפח אחסון SSD של 40 GB',
      'תעבורה חודשית של 20 TB',
      '100 קרדיטים לפיתוח AI',
      'סביבת עבודה (Workspace)',
    ],
  },
  {
    name: 'GenAI Dedicated Edge',
    tag: 'הפופולרית ביותר',
    price: '₪ 399',
    description: 'לפרויקטים בצמיחה, אפליקציות עסקיות, עבודת לקוחות וסביבות שדורשות יותר כוח.',
    cta: 'בחרו ב-Dedicated Edge',
    href: 'https://my.epicfinal.com/index.php?rp=/store/managed-genai-software-builder/genai-software-builder-genai-edge&language=hebrew&currency=2',
    featured: true,
    specs: [
      'סביבת VM ייעודית',
      '4 vCPU',
      '8 GB RAM',
      'נפח אחסון SSD של 160 GB',
      'תעבורה חודשית של 20 TB',
      '100 קרדיטים לפיתוח AI',
      'סביבת עבודה (Workspace)',
    ],
  },
  {
    name: 'GenAI Dedicated Supreme',
    tag: 'לביצועים גבוהים יותר',
    price: '₪ 849',
    description: 'לאפליקציות מורכבות יותר, עומסי עבודה כבדים ופרויקטים שדורשים משאבים חזקים יותר.',
    cta: 'שדרגו ל-Dedicated Supreme',
    href: 'https://my.epicfinal.com/index.php?rp=/store/managed-genai-software-builder/genai-supreme&language=hebrew&currency=2',
    specs: [
      'סביבת VM ייעודית',
      '8 vCPU',
      '16 GB RAM',
      'נפח אחסון SSD של 320 GB',
      'תעבורה חודשית של 20 TB',
      '100 קרדיטים לפיתוח AI',
      'סביבת עבודה (Workspace)',
    ],
  },
];

const upgrades = [
  { amount: '500 קרדיטים לפיתוח AI', price: 'תוספת של 199 ₪ לחודש' },
  { amount: '2,000 קרדיטים לפיתוח AI', price: 'תוספת של 749 ₪ לחודש' },
];

const faqItems = [
  {
    question: 'מה זה GenAI?',
    answer:
      'GenAI היא סביבה מבוססת בינה מלאכותית לבנייה, שיפור, בדיקה והרצה של אפליקציות. היא משלבת פיתוח בעזרת AI, סביבת עבודה (Workspace) והעלאה לאוויר בסביבת ענן.',
  },
  {
    question: 'מה ההבדל בין GenAI Cloud ל-GenAI Dedicated?',
    answer:
      'GenAI Cloud היא סביבת ענן מנוהלת להתחלה מהירה. היא מתאימה ביותר לדפי נחיתה, אתרים פשוטים, אפליקציות קלות ו-MVPs מוקדמים. GenAI Dedicated כוללת VM ייעודי עם CPU, RAM, אחסון SSD ותעבורה מוגדרים מראש, ומתאימה יותר לפרויקטים שדורשים בידוד חזק יותר, משאבים צפויים או עומסי עבודה כבדים יותר.',
  },
  {
    question: 'האם GenAI Cloud כוללת VM ייעודי?',
    answer:
      'לא. GenAI Cloud אינה כוללת VM ייעודי. היא רצה על תשתית ענן מנוהלת עם סביבות נפרדות לכל לקוח. אם אתם צריכים VM ייעודי, בחרו ב-GenAI Dedicated.',
  },
  {
    question: 'האם אפשר לחבר מסד נתונים או שירות אחסון חיצוני?',
    answer:
      'כן. GenAI Cloud יכולה להתחבר למסדי נתונים חיצוניים, שירותי אחסון ו-APIs.',
  },
  {
    question: 'האם GenAI Cloud כוללת אחסון קבוע (Persistent Storage)?',
    answer:
      'GenAI Cloud אינה כוללת מסד נתונים פנימי קבוע או אחסון קבצים מקומי קבוע כברירת מחדל. עבור נתונים, העלאות או קבצים שחייבים להישאר זמינים באופן קבוע, השתמשו במסד נתונים/שירות אחסון חיצוני או בחרו ב-GenAI Dedicated כאשר נדרש אחסון פנימי.',
  },
  {
    question: 'מתי כדאי לי לבחור ב-GenAI Dedicated?',
    answer:
      'בחרו ב-GenAI Dedicated כאשר הפרויקט שלכם דורש VM ייעודי, בידוד חזק יותר, משאבים צפויים, עומסי עבודה כבדים יותר, אחסון פנימי או שליטה מתקדמת יותר.',
  },
  {
    question: 'האם אפשר לשדרג מאוחר יותר?',
    answer:
      'כן. תוכלו להתחיל עם GenAI Cloud ולעבור ל-GenAI Dedicated כשהפרויקט שלכם יצטרך יותר משאבים או VM ייעודי.',
  },
  {
    question: 'מהם קרדיטים לפיתוח AI?',
    answer:
      'קרדיטים לפיתוח AI משמשים כאשר אתם מבקשים מה-AI לבנות, לשפר, לתקן או לעדכן את האפליקציה שלכם.',
  },
  {
    question: 'מה קורה אם נגמרים לי הקרדיטים לפיתוח AI?',
    answer:
      'תוכלו להוסיף חבילת קרדיטים חודשית לפיתוח AI בהתאם לשימוש שלכם.',
  },
];

const sectionMotion = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.55, ease: 'easeOut' },
};

export default function GenAIHe() {
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="genai-page rtl-section">
      <Helmet>
        <title>GenAI - בונה אפליקציות AI עם סביבת ענן או סביבה ייעודית | EpicFinal</title>
        <meta
          name="description"
          content="בנו אפליקציות עם AI, בדקו אותן בסביבות עבודה, והריצו אותן על GenAI Cloud או GenAI Dedicated. התחילו פשוט ושדרגו למשאבים ייעודיים כשהפרויקט שלכם יצטרך אותם."
        />
        <link rel="canonical" href="https://epicfinal.com/he/genai" />
      </Helmet>

      <section className="genai-hero">
        <Container>
          <motion.div className="genai-hero-shell" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: 'easeOut' }}>
            <div className="genai-hero-grid">
              <div className="genai-hero-copy"> 
                <div className="genai-eyebrow">GenAI</div>
                <h1 className="genai-hero-title genai-hero-title--wide">
                  <span className="genai-hero-keyword genai-hero-keyword--build">בונים</span> אפליקציות עם AI.{' '}
                  <span className="genai-hero-keyword genai-hero-keyword--test">בודקים</span> ב-Workspace.{' '}
                  <span className="genai-hero-keyword genai-hero-keyword--deploy">מריצים</span> בדרך שלכם.
                </h1>
                <p className="genai-hero-text">
                  מהרעיון הראשון ועד אפליקציה פעילה — בנו, בדקו ושפרו בעזרת AI, בלי להתחיל מתשתיות או מפיתוח קוד מאפס, והעלו לאוויר בלחיצה אחת.
                </p>
                <p className="genai-hero-text genai-hero-text-secondary">
                  התחילו בקלות עם GenAI Cloud, ועברו ל־GenAI Dedicated כשהפרויקט גדל וצריך יותר כוח, בידוד ומשאבים ייעודיים.
                </p>

                <div className="genai-hero-actions">
                  <a
                    href={cloudStarterPlan.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="genai-btn genai-btn-primary"
                  >
                    התחילו עכשיו
                    <FiArrowLeft aria-hidden="true" />
                  </a>
                  <a href="#genai-pricing" className="genai-btn genai-btn-secondary">
                    ראו חבילות
                  </a>
                </div>

                <p className="genai-hero-note">
                  אידיאלי לדפי נחיתה, MVPs, כלים עסקיים, מערכות פנימיות, פרויקטים ללקוחות וסביבות פיתוח מהירות.
                </p>
              </div>

              <div className="genai-hero-visual" aria-hidden="true">
                <div className="genai-workspace">
                  <div className="genai-workspace-top">
                    <div className="genai-window-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <div className="genai-visual-badge">סביבת ענן או סביבה ייעודית</div>
                  </div>

                  <div className="genai-prompt-box">
                    <span className="genai-prompt-label">Prompt</span>
                    <p>בנה לי פורטל לקוחות עם התחברות, דשבורד, טבלת הזמנות ואזור ניהול.</p>
                  </div>

                  <div className="genai-visual-steps">
                    {['תארו את הרעיון', 'בנו בעזרת AI', 'בדקו בסביבת העבודה', 'השיקו לאוויר'].map((item, index) => (
                      <div key={item} className="genai-visual-step">
                        <span className="genai-visual-step-index">0{index + 1}</span>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>

                  <div className="genai-visual-caption">
                    סביבות נפרדות לבנייה, תצוגה מקדימה ופרודקשן.
                  </div>
                </div>
              </div>
            </div>

            <div className="genai-value-strip">
              {valueStrip.map((item) => (
                <div key={item} className="genai-value-item">
                  <FiCheck aria-hidden="true" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </Container>
      </section>

      <section className="genai-subnav">
        <Container>
          <div className="genai-subnav-track">
            {sectionLinks.map((item) => (
              <a key={item.href} href={item.href} className="genai-subnav-link">
                {item.label}
              </a>
            ))}
          </div>
        </Container>
      </section>

      <section id="genai-features" className="genai-section">
        <Container>
          <motion.div className="genai-promise-grid" {...sectionMotion}>
            <div className="genai-section-copy">
              <h2 className="genai-section-title genai-section-title--wide">מהרעיון הראשון לאפליקציה עובדת</h2>
              <p className="genai-section-text">
                GenAI עוזרת לכם לעבור מרעיון לאפליקציה עובדת בלי להפוך את התשתית להחלטה הראשונה.
              </p>
              <p className="genai-section-text">
                תארו מה אתם רוצים לבנות, שפרו את זה עם AI, בדקו שינויים בסביבת העבודה, והשיקו את הפרויקט בסביבה שמתאימה לשלב שלכם.
              </p>
            </div>

            <div className="genai-stat-grid">
              {promiseStats.map((item) => (
                <div key={item.label} className="genai-stat-card">
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </Container>
      </section>

      <section id="genai-how-it-works" className="genai-section genai-section-alt">
        <Container>
          <motion.div {...sectionMotion}>
            <div className="genai-section-heading">
              <div className="genai-section-kicker">איך זה עובד</div>
              <h2 className="genai-section-title">איך GenAI עובד</h2>
            </div>

            <div className="genai-steps-grid">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={step.title} className="genai-step-card">
                    <div className="genai-step-topline">
                      <span className="genai-step-number">שלב {index + 1}</span>
                      <Icon aria-hidden="true" />
                    </div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </Container>
      </section>

      <section className="genai-section">
        <Container>
          <motion.div {...sectionMotion}>
            <div className="genai-section-heading">
              <div className="genai-section-kicker">למה GenAI</div>
              <h2 className="genai-section-title">למה לבחור ב-GenAI?</h2>
            </div>

            <div className="genai-benefits-grid">
              {benefits.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="genai-benefit-card">
                    <div className="genai-benefit-icon">
                      <Icon aria-hidden="true" />
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </Container>
      </section>

      <section className="genai-section genai-compare-section">
        <Container>
          <motion.div {...sectionMotion}>
            <div className="genai-section-heading">
              <div className="genai-section-kicker">הדרך הישנה מול GenAI</div>
              <h2 className="genai-section-title">פחות הגדרות. בדיקות מהירות יותר. פחות חיכוך תשתיתי.</h2>
            </div>

            <div className="genai-compare-grid">
              <div className="genai-compare-panel">
                <h3>הדרך הישנה</h3>
                <ul>
                  {oldWay.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="genai-compare-panel genai-compare-panel-accent">
                <h3>עם GenAI</h3>
                <ul>
                  {genaiWay.map((item) => (
                    <li key={item}>
                      <FiCheck aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      <section id="genai-audience" className="genai-section">
        <Container>
          <motion.div {...sectionMotion}>
            <div className="genai-section-heading">
              <div className="genai-section-kicker">למי זה מיועד</div>
              <h2 className="genai-section-title">למי GenAI מתאימה</h2>
            </div>

            <div className="genai-audience-grid">
              {audiences.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="genai-audience-card">
                    <div className="genai-audience-icon">
                      <Icon aria-hidden="true" />
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </Container>
      </section>

      <section id="genai-use-cases" className="genai-section genai-section-alt">
        <Container>
          <motion.div {...sectionMotion}>
            <div className="genai-section-heading">
              <div className="genai-section-kicker">שימושים</div>
              <h2 className="genai-section-title genai-section-title--wide">בחרו את הסביבה הנכונה לשלב הפרויקט שלכם</h2>
              <p className="genai-section-subtitle">
                התחילו פשוט עם GenAI Cloud, ואז עברו ל-GenAI Dedicated כשהפרויקט שלכם יצטרך משאבים חזקים יותר, יותר בידוד או VM ייעודי.
              </p>
            </div>

            <div className="genai-usecase-groups">
              {useCaseGroups.map((group) => (
                <div
                  key={group.title}
                  className={`genai-usecase-group-card ${
                    group.title === 'GenAI Cloud'
                      ? 'genai-usecase-group-card--cloud'
                      : 'genai-usecase-group-card--dedicated'
                  }`}
                >
                  <h3>{group.title}</h3>
                  <p className="genai-usecase-group-subtitle">{group.subtitle}</p>
                  <p className="genai-usecase-group-description">{group.description}</p>
                  <div className="genai-usecase-cloud">
                    {group.items.map((item) => (
                      <span key={item} className="genai-usecase-pill">
                        {item}
                      </span>
                    ))}
                  </div>
                  <div className="genai-usecase-tags">
                    {group.tags.map((tag) => (
                      <span key={tag} className="genai-usecase-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <p className="genai-option-summary">התחילו עם Cloud. עברו ל-Dedicated כשהפרויקט שלכם הופך לרציני יותר.</p>
          </motion.div>
        </Container>
      </section>

      <section className="genai-section genai-two-options-section">
        <Container>
          <motion.div {...sectionMotion}>
            <div className="genai-section-heading">
              <div className="genai-section-kicker">שתי אפשרויות ריצה</div>
              <h2 className="genai-section-title genai-section-title--wide">התחילו מהר עם GenAI Cloud. שדרגו כשהפרויקט צריך יותר.</h2>
            </div>

            <div className="genai-two-options-grid">
              {optionCards.map((item) => (
                <div
                  key={item.title}
                  className={`genai-option-card${item.title === 'GenAI Cloud' ? ' genai-option-card--cloud' : ' genai-option-card--private'}`}
                >
                  <span className="genai-option-badge">{item.badge}</span>
                  <h3>{item.title}</h3>
                  <p className="genai-option-description">{item.description}</p>
                  <ul className="genai-option-list">
                    {item.points.map((point) => (
                      <li key={point}>
                        <FiCheck aria-hidden="true" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <p className="genai-option-summary">
              רוב הפרויקטים יכולים להתחיל עם GenAI Cloud. כשהאפליקציה שלכם תצטרך משאבים ייעודיים, בידוד חזק יותר או ביצועים צפויים יותר, GenAI Dedicated תספק לכם סביבת VM ייעודית.
            </p>
          </motion.div>
        </Container>
      </section>

      <section id="genai-pricing" className="genai-section">
        <Container>
          <motion.div {...sectionMotion}>
            <div className="genai-section-heading">
              <div className="genai-section-kicker">תמחור</div>
              <h2 className="genai-section-title genai-section-title--wide">בחרו את סביבת GenAI שמתאימה לשלב שלכם</h2>
              <p className="genai-section-subtitle">
                התחילו עם GenAI Cloud לסביבה מנוהלת ופשוטה. בחרו ב-GenAI Dedicated כשהפרויקט שלכם יצטרך VM ייעודי, בידוד חזק יותר ומשאבי שרת מוגדרים.
              </p>
            </div>

            <div className="genai-cloud-plan-card">
              <div className="genai-cloud-plan-shell">
                <div className="genai-plan-top">
                  <span className="genai-plan-tag">{cloudStarterPlan.tag}</span>
                  <h3>{cloudStarterPlan.name}</h3>
                  <div className="genai-plan-price">
                    <strong>{cloudStarterPlan.price}</strong>
                    <span>לחודש</span>
                  </div>
                  <p>{cloudStarterPlan.description}</p>
                </div>

                <ul className="genai-plan-list">
                  {cloudStarterPlan.specs.map((item) => (
                    <li key={item}>
                      <FiCheck aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={cloudStarterPlan.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="genai-btn genai-btn-primary"
                >
                  {cloudStarterPlan.cta}
                </a>
              </div>
            </div>

            <div className="genai-private-plans-heading">
              <div className="genai-section-kicker">GenAI Dedicated - חבילות VM ייעודי</div>
              <h3>לפרויקטים שדורשים משאבים חזקים יותר, יותר בידוד וסביבת VM ייעודית.</h3>
            </div>

            <div className="genai-pricing-grid">
              {privatePlans.map((plan) => (
                <div key={plan.name} className={`genai-plan-card${plan.featured ? ' is-featured' : ''}`}>
                  <div className="genai-plan-top">
                    {plan.tag ? <span className="genai-plan-tag">{plan.tag}</span> : null}
                    <h3>{plan.name}</h3>
                    <div className="genai-plan-price">
                      <strong>{plan.price}</strong>
                      <span>לחודש</span>
                    </div>
                    <p>{plan.description}</p>
                  </div>

                  <ul className="genai-plan-list">
                    {plan.specs.map((item) => (
                      <li key={item}>
                        <FiCheck aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href={plan.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`genai-btn ${plan.featured ? 'genai-btn-primary' : 'genai-btn-secondary'}`}
                  >
                    {plan.cta}
                  </a>
                </div>
              ))}
            </div>

            <div className="genai-upgrades-block">
              <div className="genai-upgrades-copy">
                <div className="genai-section-kicker">שדרוגי קרדיטים לפיתוח AI</div>
                <h3>צריכים עוד קרדיטים לפיתוח AI?</h3>
                <p>
                  כל חבילה כוללת 100 קרדיטים לפיתוח AI. אם אתם בונים יותר, משפרים יותר או עובדים על פרויקטים פעילים יותר, תוכלו להוסיף חבילת קרדיטים חודשית.
                </p>
                <small>קרדיטים לפיתוח AI משמשים לבנייה, שיפור, תיקון ועדכון של האפליקציה שלכם בעזרת AI.</small>
              </div>

              <div className="genai-upgrades-list">
                {upgrades.map((item) => (
                  <div key={item.amount} className="genai-upgrade-card">
                    <strong>{item.amount}</strong>
                    <span>{item.price}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="genai-clarity-panel">
              <div>
                <div className="genai-section-kicker">אמון / שקיפות</div>
                <h3>תשתית שקופה. שימוש גמיש.</h3>
                <p>
                  GenAI נועדה להפוך את הבנייה וההרצה של אפליקציות לפשוטות יותר, תוך שמירה על שקיפות בפרטים החשובים: איזו סביבה אתם מקבלים, מה כלול, ואיך עובדים הקרדיטים לפיתוח AI.
                </p>
              </div>

              <ul className="genai-check-list">
                <li><FiCheck aria-hidden="true" /><span>התחילו עם GenAI Cloud מנוהל</span></li>
                <li><FiCheck aria-hidden="true" /><span>שדרגו ל-VM ייעודי כשצריך</span></li>
                <li><FiCheck aria-hidden="true" /><span>VM ייעודי זמין בחבילות GenAI Dedicated</span></li>
                <li><FiCheck aria-hidden="true" /><span>המשאבים מוצגים בצורה ברורה</span></li>
                <li><FiCheck aria-hidden="true" /><span>הוסיפו קרדיטים לפיתוח AI כשצריך</span></li>
                <li><FiCheck aria-hidden="true" /><span>בחרו את הסביבה בהתאם לשלב הפרויקט שלכם</span></li>
              </ul>
            </div>
          </motion.div>
        </Container>
      </section>

      <section id="genai-faq" className="genai-section genai-section-alt">
        <Container>
          <motion.div {...sectionMotion}>
            <div className="genai-section-heading">
              <div className="genai-section-kicker">שאלות נפוצות</div>
              <h2 className="genai-section-title">שאלות נפוצות</h2>
            </div>

            <div className="genai-faq-list">
              {faqItems.map((item, index) => {
                const isOpen = openFaqIndex === index;
                return (
                  <div key={item.question} className={`genai-faq-item${isOpen ? ' is-open' : ''}`}>
                    <button
                      type="button"
                      className="genai-faq-question"
                      onClick={() => setOpenFaqIndex(isOpen ? -1 : index)}
                      aria-expanded={isOpen}
                      aria-controls={`genai-faq-panel-${index}`}
                    >
                      <span>{item.question}</span>
                      <FiChevronDown aria-hidden="true" />
                    </button>
                    <div id={`genai-faq-panel-${index}`} className="genai-faq-answer">
                      <p>{item.answer}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </Container>
      </section>

      <section className="genai-cta-section">
        <Container>
          <motion.div className="genai-cta-shell" {...sectionMotion}>
            <div>
              <div className="genai-section-kicker">קריאה לפעולה</div>
              <h2>יש לכם רעיון לאפליקציה? עכשיו אתם צריכים סביבה שתעזור להפוך אותו למוצר עובד.</h2>
              <p>
                GenAI משלבת בנייה מבוססת AI, סביבת עבודה לאפליקציות ואפשרויות ריצה גמישות כדי שתוכלו לנוע מהר יותר מרעיון לגרסה שניתן לבדוק ולהריץ.
              </p>
              <p>
                התחילו עם GenAI Cloud להגדרה מנוהלת ופשוטה, או בחרו ב-GenAI Dedicated כשהפרויקט שלכם יצטרך VM ייעודי.
              </p>
            </div>

            <div className="genai-cta-actions">
              <a href="#genai-pricing" className="genai-btn genai-btn-primary">
                בחרו חבילה
              </a>
              <a
                href="https://meetings.hubspot.com/viacheslav-seifer"
                target="_blank"
                rel="noopener noreferrer"
                className="genai-btn genai-btn-secondary"
              >
                צרו קשר
              </a>
            </div>
          </motion.div>
        </Container>
      </section>
    </div>
  );
}
